import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { 
  useTheme, 
  useMediaQuery, 
  Box,  
  CircularProgress, 
  Typography, 
  Tooltip, 
  IconButton, 
} from '@mui/material';
import {
  AddCircleOutline,
  Edit,
  Delete,
  DeleteForever,
  GroupAdd,
  EventNote,
  FileDownload,
} from '@mui/icons-material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { 
  buildPermissionMap, 
  hasRight,
} from 'state/checkPermission';
import { 
  capitalizeFirstLetter, 
  getSelectedIds, 
  isAnyRowSelected 
} from 'state/helpers';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import CardList from 'components/CardList';
import DataGridComponent from 'components/DataGridComponent';
import ScrollToTop from 'components/ScrollToTop';
import EnrollmentDialog from './Enrollment';
import TrainingSessionsDialog from './TrainingSessionsDialog';


const TrainingCourses = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const params = useSelector((state) => state.params); 
  const user = useSelector((state) => state.user); 
  const theme = useTheme();
  
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const permissionMap = useMemo(
    () => buildPermissionMap(user?.permissions),
    [user?.permissions]
  );
  const canCreate = hasRight(permissionMap, "TrainingCourses", "create", false);
  const canEdit = hasRight(permissionMap, "TrainingCourses", "update", false);
  const canDelete = hasRight(permissionMap, "TrainingCourses", "delete", false);
  const canEnroll = hasRight(permissionMap, "TrainingCourses", "enroll", false);
  const canSession = hasRight(permissionMap, "TrainingCourses", "session", false);

  const { startDate, endDate } = useDateRangeContext();

  const scrollRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });

  const [enrollmentModalOpen, setEnrollmentModalOpen] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState(null);

  const [sessionsDialogOpen, setSessionsDialogOpen] = useState(false);
  const [sessionsTarget, setSessionsTarget] = useState({ id: null, name: '', startDate: null, endDate: null, startTime: null, endTime: null });

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null); // 'single' or 'bulk'
  const [idToDelete, setIdToDelete] = useState(null);
  const [trainingCourses, setTrainingCourses] = useState([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });

  const paramsMap = useMemo(
    () => new Map(params?.map(p => [p.param, p.value]) ?? []),
    [params]
  );
  const TIMEZONE_OFFSET_MINUTES = Number(paramsMap.get("TIMEZONE_OFFSET_MINUTES")) || 420;   
  
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchTrainingCourses = async () => {
      if (!startDate || !endDate) return;

      try {
        setLoading(true);  
        const url = '/trainingcourses';
        const payload = { startDate, endDate };
        
        const res = await api.post(url, payload);
        
        isMounted && setTrainingCourses(res.data);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTrainingCourses();

    return () => {
      isMounted = false;
      controller.abort();
    }
  }, [startDate, endDate]); 

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,      
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      ...(() => {
        return isMobile ? { width: 60 } : { flex: 1 };
      })(),
    },
    { 
      field: "courseName", 
      headerName: "CourseName", 
      ...(() => {
        return isMobile ? { width: 200 } : { flex: 3 };
      })(),
    },    
    {
      field: "crewScheduleCode",
      headerName: "ScheduleCode",
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.8 };
      })(), 
    },
    { 
      field: "description", 
      headerName: "Description", 
      ...(() => {
        return isMobile ? { width: 200 } : { flex: 3 };
      })(),
    },
    {
      field: "startDate",
      headerName: "StartDate",
      valueFormatter: (value) => {
        if (value == null) {
          return '';
        }
        return `${format(value, "dd/MM/yyyy")}`;
      },
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(),
    },
    {
      field: "endDate",
      headerName: "EndDate",
      valueFormatter: (value) => {
        if (value == null) {
          return '';
        }
        return `${format(value, "dd/MM/yyyy")}`;
      },
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(),
    },
    {
      field: "startTime",
      headerName: "StartTime",
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(),
    },
    {
      field: "endTime",
      headerName: "EndTime",
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(),
    },
    {
      field: "status",
      headerName: "Status",
      valueFormatter: (value) => value
        ? capitalizeFirstLetter(value)
        : '',
      ...(() => {
        return isMobile ? { width: 80 } : { flex: 1.2 };
      })(),
    },
  ], [isMobile]);

  const actionColumn = useMemo(() => {    
    const activeCount = [
      canEdit, 
      // canEnroll, 
      canSession, 
      canDelete
    ].filter(Boolean).length;
    if (activeCount === 0) return [];

    const baseWidth = 65 + activeCount * 25;

    return [
      {
        field: "action",
        headerName: "Action",
        filterable: false,      
        sortable: false,
        width: baseWidth,
        renderCell: (params) => {        
          return (
            <FlexCenter
              gap={0.5}
              sx={{ width: '100%', height: '100%' }}
            >
              {canEdit && (
                <Tooltip title="Edit">
                  <IconButton
                    aria-label="Edit"
                    onClick={() => {
                      if (!canEdit) return;
                      navigate(`/trainingcourses/${params.row._id}`);
                    }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
              )}

              {canEnroll && (
                <Tooltip title="Enroll">
                  <IconButton
                    aria-label="Enroll"
                    onClick={() => {
                      if (!canEnroll) return;
                      handleEnroll(params.row._id);
                    }}
                    sx={{ color: "success.main", display: "none" }}
                  >
                    <GroupAdd />
                  </IconButton>
                </Tooltip>
              )}

              {canSession && (
                <Tooltip title="Sessions">
                  <IconButton
                    aria-label="Sessions"
                    sx={{ color: "info.main" }}
                    onClick={() => {
                      setSessionsTarget({
                        id: params.row._id,
                        name: params.row.courseName,
                        startDate: params.row.startDate || null,
                        endDate:   params.row.endDate   || null,
                        startTime: params.row.startTime || null,
                        endTime:   params.row.endTime   || null,
                        subjects:  params.row.subjects  || [],
                      });
                      setSessionsDialogOpen(true);
                    }}
                  >
                    <EventNote />
                  </IconButton>
                </Tooltip>
              )}

              {canDelete && (
                <Tooltip title="Delete">
                  <IconButton
                    aria-label="Delete"
                    sx={{ color: "error.main" }}
                    onClick={() => {
                      if (!canDelete) return;
                      handleDeleteClick(params.row._id);
                    }}
                  >
                    <DeleteForever />
                  </IconButton>                
                </Tooltip>    
              )}      
            </FlexCenter>
          );
        },
      },
    ];
  }, [
    navigate, 
    canEdit,
    canEnroll,
    canSession,
    canDelete,
  ]);
  
  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, trainingCourses);    
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }
    
    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {
      if (!canDelete) return;

      if (deleteType === 'single') {
        if (!idToDelete) return;
        
        await api.delete(`/trainingcourses/delete/${idToDelete}`);

        setTrainingCourses(trainingCourses.filter((item) => item._id !== idToDelete));
        toast.success('Training course deleted successfully.');
      } else if (deleteType === 'bulk') {
        const selectedIds = getSelectedIds(rowSelectionModel, trainingCourses);
        if (selectedIds.length === 0) {
          toast.error('No rows selected for deletion!');
          return;
        }

        const url = '/trainingcourses/soft-delete';
        const payload = { ids: selectedIds };        
        const res = await api.patch(url, payload);        
        toast.success(res.data?.message || `${selectedIds.length} record(s) deleted successfully.`);
        
        setTrainingCourses(trainingCourses.filter((item) => !selectedIds.includes(item._id)));
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete training courses!");
    }
  }, [
    canDelete, 
    deleteType, 
    idToDelete, 
    rowSelectionModel, 
    trainingCourses
  ]);
 
  const handleEnroll = (trainingCourseId) => {
    setSelectedCourseId(trainingCourseId);
    setEnrollmentModalOpen(true);
  };

  const submitEnrollment = async (trainingCourseId, enrollmentData) => {
    try {
      const payload = {
        enrollments: enrollmentData?.map(member => ({
          employeeCode: member.employeeCode || '',
          employeeName: member.employeeName || '',
          rank: member.rank || '',
          base: member.base || '',
          enrolledAs: member.enrolledAs || 'trainee'
        }))
      };

      await api.patch(`/trainingcourses/enroll/${trainingCourseId}`, payload);

    } catch (error) {
      toast.error(error?.response?.data?.message || 'Enrollment failed.');
    }
  };


  const handleExportToAIMS = async () => {
    const selectedIds = [...rowSelectionModel.ids];
    if (selectedIds.length !== 1) {
      toast.error('Select exactly one course to export.');
      return;
    }
    const courseId = selectedIds[0];
    let courseDetail;
    try {
      const res = await api.get(`/trainingcourses/${courseId}`);
      courseDetail = res.data;
    } catch {
      toast.error('Failed to load course detail.');
      return;
    }
    const { crewScheduleCode, startDate, endDate, startTime, endTime, enrollments = [] } = courseDetail;
    if (!startDate || !endDate || !startTime || !endTime) {
      toast.error('Course dates and times are required for export.');
      return;
    }
    if (!crewScheduleCode) { toast.error('Course has no schedule code.'); return; }
    if (enrollments.length === 0) { toast.error('No enrolled employees to export.'); return; }

    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    const localStartMins = sh * 60 + sm;
    const localEndMins   = eh * 60 + em;

    let dutyMins = localEndMins - localStartMins;
    if (dutyMins <= 0) dutyMins += 1440;
    const pad2 = (n) => String(n).padStart(2, '0');
    const dutyStr = `${pad2(Math.floor(dutyMins / 60))}${pad2(dutyMins % 60)}`;

    const fmtMins = (mins) => {
      const m = ((mins % 1440) + 1440) % 1440;
      return `${pad2(Math.floor(m / 60))}${pad2(m % 60)}`;
    };
    const utcStartStr = fmtMins(localStartMins - TIMEZONE_OFFSET_MINUTES);
    const utcEndStr   = fmtMins(localEndMins   - TIMEZONE_OFFSET_MINUTES);

    const toLocalDate = (d) => {
      const date = new Date(d);
      return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    };
    const startLocal = toLocalDate(startDate);
    const endLocal   = toLocalDate(endDate);

    const lines = [];
    for (const emp of enrollments) {
      const cur = new Date(startLocal);
      while (cur <= endLocal) {
        const utcMs   = cur.getTime() + localStartMins * 60000 - TIMEZONE_OFFSET_MINUTES * 60000;
        const utcDate = new Date(utcMs);
        const dateStr = `${pad2(utcDate.getUTCDate())}${pad2(utcDate.getUTCMonth() + 1)}${utcDate.getUTCFullYear()}`;
        lines.push(`${emp.employeeCode},${dateStr},${crewScheduleCode},,${dutyStr},,${utcStartStr},${utcEndStr}`);
        cur.setDate(cur.getDate() + 1);
      }
    }

    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${crewScheduleCode}_${pad2(startLocal.getDate())}${pad2(startLocal.getMonth() + 1)}${startLocal.getFullYear()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Training Courses" 
          subtitle={`${loading ? 'Loading...' : 'List of Courses'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>        
            <Tooltip title="Export to text file" arrow>
              <span>
                <StyledButton
                  disabled={rowSelectionModel.ids.size !== 1}
                  onClick={handleExportToAIMS}
                >
                  <FileDownload />
                </StyledButton>
              </span>
            </Tooltip>

            {canDelete && (
              <Tooltip title="Bulk delete" arrow>
                <span>
                  <StyledButton 
                    disabled={!isAnyRowSelected(rowSelectionModel, trainingCourses)}
                    onClick={() => {
                      if (!canDelete) return;
                      handleBulkDelete();
                    }} 
                  >
                    <Delete />
                  </StyledButton>
                </span>
              </Tooltip>
            )}

            {canCreate && (
              <Tooltip title="Create" arrow>
                <span>
                  <StyledButton 
                    onClick={() => {
                      if (!canCreate) return;
                      navigate("/trainingcourses/new");
                    }}
                  >
                    <AddCircleOutline />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          gap={2}
        >
          <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
          >
            {isMobile ? (
              <CardList
                rows={trainingCourses}
                primaryField="courseName"
                secondaryField="description"
                isActiveField={null}
                statusChip={{
                  field: 'status',
                  colorMap: { planning: 'warning', published: 'success', archived: 'default' },
                }}
                fields={[
                  { label: "Code", key: "crewScheduleCode" },
                  { label: "Start Date", key: "startDate" },
                  { label: "End Date", key: "endDate" },
                  { label: "Start Time", key: "startTime" },
                  { label: "End Time", key: "endTime" },
                ]}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={(id) => navigate(`/trainingcourses/${id}`)}
                onDelete={handleDeleteClick}
              />
            ) : (
              <DataGridComponent
                rows={trainingCourses}
                columns={columns.concat(actionColumn)}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
                props={{
                  checkboxSelection: true,
                  disableRowSelectionOnClick: true,
                  rowSelectionModel,
                  onRowSelectionModelChange: setRowSelectionModel,
                  getRowClassName: (params) => params.row.status === 'Archived' ? 'isArchived' : '',
                  initialState: { pagination: { paginationModel: { pageSize: 100 } } },
                  sx: {
                    "& .MuiDataGrid-row.Archived": {
                      backgroundColor: `${theme.palette.primary.light} !important`,  
                    },
                  },
                }}
              />
            )}
            <ScrollToTop scrollRef={scrollRef} />
          </Box>
        </Box>
      )}

      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />

      {enrollmentModalOpen && (
        <EnrollmentDialog
          open={enrollmentModalOpen}
          onClose={() => setEnrollmentModalOpen(false)}
          onSubmit={submitEnrollment}
          trainingCourseId={selectedCourseId}
        />
      )}

      <TrainingSessionsDialog
        open={sessionsDialogOpen}
        onClose={() => setSessionsDialogOpen(false)}
        courseId={sessionsTarget.id}
        courseName={sessionsTarget.name}
        course={sessionsTarget}
      />
    </Box>
  );
};

export default TrainingCourses;
